/**
 * Gender Party Photo Upload Hook
 *
 * Handles photo uploads for gender party carousel.
 * Supports multi-file upload with per-batch progress tracking.
 * Uses multipart form data to upload directly to API.
 */

import { useState, useCallback, useRef } from "react";
import { config } from "../../../../config";
import { getAuthHeaders } from "../../../../lib/api-headers";
import { haptic, safeShowAlert } from "../../../../lib/telegram";
import { safeParseResponseJson } from "../../../../lib/json-utils";
import { getErrorMessage } from "../../../../lib/graphql";

export interface UploadedPhoto {
  id: string;
  imageUrl: string;
  caption: string | null;
  sortOrder: number;
}

export interface UploadProgress {
  current: number;
  total: number;
}

interface UseGenderPartyPhotoUploadOptions {
  genderPartyId: string | undefined;
  onSuccess?: (photo: UploadedPhoto) => void;
  onBatchComplete?: (photos: UploadedPhoto[]) => void;
}

interface UseGenderPartyPhotoUploadResult {
  isUploading: boolean;
  uploadProgress: UploadProgress | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  uploadPhoto: (file: File) => Promise<UploadedPhoto | null>;
  uploadPhotos: (files: File[]) => Promise<UploadedPhoto[]>;
  triggerFileSelect: () => void;
}

async function uploadSingleFile(
  genderPartyId: string,
  file: File
): Promise<UploadedPhoto> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${config.apiUrl}/upload/gender-party-photo/${genderPartyId}`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: formData,
    }
  );

  if (!response.ok) {
    const data = await safeParseResponseJson<{ error?: string }>(
      response,
      "photo upload error"
    );
    throw new Error(data.error || "Ошибка загрузки");
  }

  const result = (await response.json()) as {
    success: boolean;
    photo: UploadedPhoto;
  };

  return result.photo;
}

export function useGenderPartyPhotoUpload(
  options: UseGenderPartyPhotoUploadOptions
): UseGenderPartyPhotoUploadResult {
  const { genderPartyId, onSuccess, onBatchComplete } = options;

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadPhoto = useCallback(
    async (file: File): Promise<UploadedPhoto | null> => {
      if (!genderPartyId) {
        safeShowAlert("ID не задан");
        return null;
      }

      setIsUploading(true);
      setUploadProgress({ current: 1, total: 1 });
      try {
        const photo = await uploadSingleFile(genderPartyId, file);
        haptic("success");
        onSuccess?.(photo);
        return photo;
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка загрузки фото"));
        return null;
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [genderPartyId, onSuccess]
  );

  const uploadPhotos = useCallback(
    async (files: File[]): Promise<UploadedPhoto[]> => {
      if (!genderPartyId) {
        safeShowAlert("ID не задан");
        return [];
      }

      if (files.length === 0) return [];

      setIsUploading(true);
      const uploaded: UploadedPhoto[] = [];
      let failedCount = 0;

      try {
        for (let i = 0; i < files.length; i++) {
          setUploadProgress({ current: i + 1, total: files.length });
          try {
            const photo = await uploadSingleFile(genderPartyId, files[i]);
            uploaded.push(photo);
            onSuccess?.(photo);
          } catch {
            failedCount++;
          }
        }

        if (failedCount > 0 && failedCount < files.length) {
          safeShowAlert(
            `Загружено ${uploaded.length} из ${files.length} фото`
          );
        } else if (failedCount === files.length) {
          haptic("error");
          safeShowAlert("Не удалось загрузить фото");
        }

        if (uploaded.length > 0) {
          haptic("success");
          onBatchComplete?.(uploaded);
        }

        return uploaded;
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [genderPartyId, onSuccess, onBatchComplete]
  );

  const triggerFileSelect = useCallback(() => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isUploading]);

  return {
    isUploading,
    uploadProgress,
    fileInputRef,
    uploadPhoto,
    uploadPhotos,
    triggerFileSelect,
  };
}

// =============================================================================
// Couple Photo Upload Hook (main photo, not carousel)
// =============================================================================

interface UseCouplePhotoUploadOptions {
  genderPartyId: string | undefined;
  onSuccess?: (url: string) => void;
}

interface UseCouplePhotoUploadResult {
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  uploadCouplePhoto: (file: File) => Promise<string | null>;
  triggerFileSelect: () => void;
}

export function useCouplePhotoUpload(
  options: UseCouplePhotoUploadOptions
): UseCouplePhotoUploadResult {
  const { genderPartyId, onSuccess } = options;

  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadCouplePhoto = useCallback(
    async (file: File): Promise<string | null> => {
      if (!genderPartyId) {
        safeShowAlert("ID не задан");
        return null;
      }

      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(
          `${config.apiUrl}/upload/gender-party-couple/${genderPartyId}`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: formData,
          }
        );

        if (!response.ok) {
          const data = await safeParseResponseJson<{ error?: string }>(
            response,
            "couple photo upload error"
          );
          throw new Error(data.error || "Ошибка загрузки");
        }

        const result = (await response.json()) as {
          success: boolean;
          url: string;
        };

        haptic("success");
        onSuccess?.(result.url);
        return result.url;
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка загрузки фото"));
        return null;
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [genderPartyId, onSuccess]
  );

  const triggerFileSelect = useCallback(() => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isUploading]);

  return {
    isUploading,
    fileInputRef,
    uploadCouplePhoto,
    triggerFileSelect,
  };
}
